import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

// Get the base API URL from environment or use localhost as fallback
let API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Remove trailing slash if present
if (API_URL.endsWith('/')) {
  API_URL = API_URL.slice(0, -1);
}

// Special handling for API URLs
if (API_URL === '/api') {
  // When REACT_APP_API_URL is just '/api', use empty string as base URL
  // This will make axios use relative URLs which will work with the current domain
  API_URL = '';
} else if (API_URL.endsWith('/api')) {
  // For other URLs ending with /api, remove it to avoid double prefixing
  API_URL = API_URL.slice(0, -4);
}

console.log('======================================');
console.log('FRONTEND API CONFIGURATION');
console.log(`Using API URL: ${API_URL}`);
console.log('======================================');

// Create a custom axios instance with detailed logging
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Don't use credentials for cross-origin requests
  withCredentials: false
});

// Function to get the Auth0 token from localStorage
const getAuthToken = () => {
  try {
    // Auth0 stores the token in localStorage with a key that includes the domain and client ID
    const auth0Key = Object.keys(localStorage).find(key => 
      key.includes('auth0') && key.includes('accessToken')
    );
    
    if (auth0Key) {
      return JSON.parse(localStorage.getItem(auth0Key));
    }
    return null;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

// Create a hook to get an authenticated API instance
export const useAuthenticatedApi = () => {
  const { getAccessTokenSilently } = useAuth0();
  
  const authenticatedApi = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  // Add a request interceptor to add the auth token to every request
  authenticatedApi.interceptors.request.use(
    async (config) => {
      try {
        // Get the access token
        const token = await getAccessTokenSilently();
        
        // Add the token to the Authorization header
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        return config;
      } catch (error) {
        console.error('Error getting access token:', error);
        return config;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  return authenticatedApi;
};

// Add request interceptor with enhanced debugging and authentication
api.interceptors.request.use(
  (config) => {
    console.log('\n======================================');
    console.log('OUTGOING API REQUEST');
    console.log(`Time: ${new Date().toISOString()}`);
    console.log(`Method: ${config.method.toUpperCase()}`);
    console.log(`URL: ${config.baseURL}${config.url}`);
    
    // Add Auth0 token to the request if available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('Headers:', JSON.stringify(config.headers, null, 2));
    
    if (config.data) {
      console.log('Request Body:', JSON.stringify(config.data, null, 2));
    }
    
    console.log('======================================\n');
    return config;
  },
  (error) => {
    console.error('\n======================================');
    console.error('API REQUEST ERROR');
    console.error(`Time: ${new Date().toISOString()}`);
    console.error('Error:', error.message);
    console.error('Stack:', error.stack);
    console.error('======================================\n');
    return Promise.reject(error);
  }
);

// Add response interceptor with enhanced debugging
api.interceptors.response.use(
  (response) => {
    console.log('\n======================================');
    console.log('INCOMING API RESPONSE');
    console.log(`Time: ${new Date().toISOString()}`);
    console.log(`Status: ${response.status} ${response.statusText}`);
    console.log(`From URL: ${response.config.baseURL}${response.config.url}`);
    console.log('Response Headers:', JSON.stringify(response.headers, null, 2));
    
    if (response.data) {
      console.log('Response Data:', JSON.stringify(response.data, null, 2));
    }
    
    console.log('======================================\n');
    return response;
  },
  (error) => {
    console.error('\n======================================');
    console.error('API RESPONSE ERROR');
    console.error(`Time: ${new Date().toISOString()}`);
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error(`Status: ${error.response.status}`);
      console.error('Response Headers:', JSON.stringify(error.response.headers, null, 2));
      console.error('Response Data:', JSON.stringify(error.response.data, null, 2));
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received');
      console.error('Request:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error Message:', error.message);
    }
    
    // Detailed CORS error logging
    if (error.message === 'Network Error') {
      console.error('\n======================================');
      console.error('CORS ERROR DETECTED');
      console.error('This is likely a CORS issue. Check the following:');
      console.error('1. Backend CORS headers are properly set');
      console.error('2. Backend is running and accessible');
      console.error('3. The URL is correct:', API_URL);
      console.error('======================================\n');
    }
    
    console.error('Error Config:', error.config);
    console.error('======================================\n');
    return Promise.reject(error);
  }
);

export default api;
